import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "design-system/molecules/Breadcrumb"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { ArrowLeft, ChevronDown } from "lucide-react"
import {
  DEFAULT_TITLE_ATTRIBUTE,
  HEADER_PORTAL_ID,
  HEADER_TITLE_PORTAL_ID,
} from "modules/shared/lib/constants"
import { CrumbActions } from "./CrumbActions"
import { CrumbTitle } from "./CrumbTitle"
import { useBreadcrumbs } from "./useBreadcrumbs"

export function Breadcrumbs() {
  const { currentCrumb, middleCrumbs, visibleCrumbs, previousCrumbs, showDropdown } =
    useBreadcrumbs()
  const navigate = useNavigate()

  const handleBack = () => {
    if (previousCrumbs.length > 0) {
      navigate({ to: previousCrumbs[previousCrumbs.length - 1]?.fullPath })
    }
  }

  return (
    <>
      <div className="flex flex-row items-center justify-start gap-2 w-full truncate">
        {/* Desktop */}
        <Breadcrumb>
          <BreadcrumbList className="flex flex-nowrap">
            {visibleCrumbs.map((crumb) => {
              return (
                <BreadcrumbItem key={crumb.fullPath} className="hidden md:flex">
                  <BreadcrumbLink asChild>
                    <Link to={crumb.fullPath} className="hover:text-foreground/80">
                      <CrumbTitle crumb={crumb} />
                    </Link>
                  </BreadcrumbLink>
                  <BreadcrumbSeparator />
                </BreadcrumbItem>
              )
            })}
            {showDropdown && middleCrumbs.length > 0 && (
              <BreadcrumbItem className="hidden md:flex">
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-1 text-interactive hover:text-interactive/80">
                    <BreadcrumbEllipsis className="h-4 w-4" />
                    <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {middleCrumbs.map((crumb) => {
                      return (
                        <DropdownMenuItem key={crumb.fullPath} asChild>
                          <Link
                            to={crumb.fullPath}
                            className="text-interactive hover:text-interactive/80"
                          >
                            <CrumbTitle crumb={crumb} />
                          </Link>
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <BreadcrumbSeparator />
              </BreadcrumbItem>
            )}
            {/* Mobile */}
            {previousCrumbs.length > 0 && (
              <Button
                onClick={handleBack}
                className="md:hidden flex items-center text-foreground flex-nowrap"
                variant="ghost"
                size="icon"
              >
                <ArrowLeft />
              </Button>
            )}
            {/* Shared */}
            {currentCrumb && (
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <div id={HEADER_TITLE_PORTAL_ID}>
                    <div id={DEFAULT_TITLE_ATTRIBUTE}>
                      <CrumbTitle crumb={currentCrumb} />
                    </div>
                  </div>
                </BreadcrumbPage>
              </BreadcrumbItem>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex flex-row-reverse gap-2 justify-center lg:justify-between">
        <CrumbActions crumb={currentCrumb} />
        <div
          id={HEADER_PORTAL_ID}
          className="flex flex-row-reverse gap-2 justify-center lg:justify-between"
        />
      </div>
    </>
  )
}
